import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@volca/ui/components/ui/hover-card";
import { cn } from "@volca/ui/lib/utils";

const objectChipVariants = cva(
  "inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-md text-ink align-middle tabular-nums outline-none transition-[background-color,color] duration-150 ease-out [&_svg]:shrink-0 [&_svg]:text-subtle focus-visible:ring-[3px] focus-visible:ring-focus/50",
  {
    variants: {
      // `pill` for a record named among other text or in a cell beside its
      // own record; `plain` for the record a row is.
      variant: {
        pill: "bg-neutral-subtle px-1.5 [a&]:hover:bg-neutral-subtle-hovered [button&]:hover:bg-neutral-subtle-hovered",
        plain: "font-medium [a&]:hover:text-link [button&]:hover:text-link",
      },
      size: {
        sm: "h-5 text-xs [&_svg]:size-3",
        default: "h-6 text-sm [&_svg]:size-3.5",
      },
    },
    defaultVariants: { variant: "pill", size: "default" },
  },
);

export interface ObjectChipProps
  extends useRender.ComponentProps<"span">,
    VariantProps<typeof objectChipVariants> {}

/**
 * A span by default. Render a button or a link
 * (`render={<button type="button" />}`) when it opens the record, and wrap
 * it in an ObjectPeek to preview it.
 */
export const ObjectChip = ({
  variant,
  size,
  className,
  render,
  ref,
  children,
  ...props
}: ObjectChipProps) =>
  useRender({
    defaultTagName: "span",
    render,
    ref,
    props: mergeProps<"span">(
      {
        "data-slot": "object-chip",
        className: cn(objectChipVariants({ variant, size }), className),
        children: <>{children}</>,
      } as React.ComponentProps<"span">,
      props,
    ),
  });

export const ObjectChipLabel = ({
  className,
  ...props
}: React.ComponentProps<"span">) => (
  <span className={cn("min-w-0 truncate", className)} {...props} />
);

/** A preview of a record on hover or focus. The full record opens on click. */
export const ObjectPeek = HoverCard;
export const ObjectPeekTrigger = HoverCardTrigger;

export const ObjectPeekContent = ({
  className,
  ...props
}: React.ComponentProps<typeof HoverCardContent>) => (
  <HoverCardContent
    align="start"
    className={cn("flex w-80 flex-col gap-3 p-3", className)}
    {...props}
  />
);

export const ObjectPeekHeader = ({
  className,
  ...props
}: React.ComponentProps<"div">) => (
  <div className={cn("flex min-w-0 flex-col gap-0.5", className)} {...props} />
);

export const ObjectPeekTitle = ({
  className,
  ...props
}: React.ComponentProps<"p">) => (
  <p
    className={cn("truncate text-sm font-medium text-ink", className)}
    {...props}
  />
);

export const ObjectPeekDescription = ({
  className,
  ...props
}: React.ComponentProps<"p">) => (
  <p className={cn("text-xs text-subtle", className)} {...props} />
);
